// The Construction tab: a see-through tab beside View. While it is in front every construction overlay shows
// (one-off poles and surfaces, planes, instance boxes, named points, voxel models; the toggles come back as they
// were when View is in front again), and a line clicked in the view picks its thing for the card's inspector:
// move / turn it with the gizmo, change its sizes, rename or drop it, each one undoable edit. The planes (op
// plane.set) are drawn here in either tab. The geometry is construction.ts; the editor (EditUI) is the host.
import type { Viewer } from '../viewer.ts';
import { type Rigid, apply, compose, identity, inverse } from '../align.ts';
import { type V3, add } from '../math.ts';
import type { GizmoHooks } from '../gizmo.ts';
import type { OpBody } from './session.ts';
import { type Mark, type State, type TypeBox, GROUP_NAME, markVal } from './log.ts';
import { type ObjInfo, boxCorners, boxEdges } from './overlay.ts';
import { type PlaneDoc, type ShapeDoc, type ShapeKind, isPolePiece, roundDoc, roundPlane } from '../fit/doc.ts';
import { SHAPE_PARAMS, shapeParamFields } from '../fit/run.ts';
import { shapeLines } from '../fit/lines.ts';
import * as cg from './construction.ts';

/** A loaded scan as the tab sees it: its id, name and state; stale: read-only (its source changed). */
export interface ConsScan { id: string; name: string; state: State; stale: boolean }
/** A named point in one frame: its scan, frame, the mark as kept, the name's own weight, where it is drawn. */
export interface ConsPoint { scan: string; f: number; mark: Mark; nw: number; q: V3 }

/** What the editor gives the tab. */
export interface ConsHost {
  v: Viewer;
  scans(): ConsScan[];
  /** Where a frame of a scan is drawn now (T · E(f), or as an alignment preview has it). */
  place(scan: string, f: number): Rigid;
  commit(label: string, bodies: Map<string, OpBody[]>): void;
  idle(): Promise<void>;
  /** True (and says why) while an edit or a job runs. */
  waits(): boolean;
  say(msg: string, warn?: boolean): void;
  /** Open the gizmo (closing any other). */
  gizmo(c: V3, title: string, hooks: GizmoHooks, opts?: { square?: boolean }): void;
  /** Where an object's publish move puts it in the world (identity: not moved); its box is drawn there. */
  moved(name: string): Rigid;
  /** Move an instance in the world with the gizmo (its points hidden, its voxels or master moving in their place). */
  moveInstance(name: string): void;
  /** Fit a one-off shape to the selected points (progress under row); resolves to the scan it is kept in, null if none was fitted. */
  fitShape(kind: ShapeKind, name: string, p: Record<string, string | number>, row: HTMLElement): Promise<string | null>;
  /** The selected points shown (at most cap), in the file coordinates of the frame most of them came from, and the scan with most of them. */
  selection(cap: number): { scan: string; frame: number; xyz: Float32Array; n: number } | null;
  screen(p: V3): [number, number] | null;
  /** The instance boxes drawn now: instance, type, corners (world). */
  boxes(): { name: string; type: string; c: V3[] }[];
  info(): ObjInfo;
  /** A type's box as the scans have it now (newest), and the scans with an instance of it (a box op goes to each). */
  typeBox(type: string): { box: TypeBox; scans: string[] } | null;
  points(): Map<string, ConsPoint[]>;
  movePoint(name: string, scan: string, f: number): void;
  dropPoint(name: string): void;
  /** Types drawn as voxel models now. */
  voxTypes(): string[];
  openType(type: string): void;
  colour(kind: 'pole' | 'surface' | 'plane'): [number, number, number, number];
  /** Every construction overlay on; the returned function puts them back as they were. */
  showAll(): () => void;
  redraw(): void;
}

type Pick =
  | { k: 'plane'; name: string; scan: string }
  | { k: 'shape'; name: string; scan: string }
  | { k: 'box'; name: string; type: string }
  | { k: 'point'; name: string }
  | { k: 'vox'; type: string };

const SEL_RGBA: [number, number, number, number] = [1, 1, 1, 1];
const CTRL_RGBA: [number, number, number, number] = [1, 0.4, 0.9, 1];
/** Most selected points a plane is fitted to (more are thinned evenly). */
/** Fitting a plane: the selection thinned to at most this many points. */
export const PLANE_MAX_PTS = 200_000;
const cm = (m: number) => +(100 * m).toFixed(1);

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};
const btn = (t: string, tip: string, go: () => void) => { const b = el('button', { class: 'small', title: tip }, t); b.onclick = go; return b; };
/** A number field (shown in unit cm, kept in m) that calls set on change. */
function num(label: string, m: number, set: (m: number) => void, tip = '', min = 0.001) {
  const f = el('label', { title: tip }, label), i = el('input', { type: 'number', step: '0.5', min: String(100 * min), style: 'width:5em' }) as HTMLInputElement;
  i.value = String(cm(m));
  i.onchange = () => { const v = +i.value / 100; if (Number.isFinite(v) && v >= min) set(v); else i.value = String(cm(m)); };
  f.append(i, document.createTextNode(' cm'));
  return f;
}

export class ConsUI {
  /** The Construction tab is in front. */
  on = false;
  private restore: (() => void) | null = null;
  private pick: Pick | null = null;
  /** Planes hidden (session only), by scan|name. */
  private hidden = new Set<string>();
  /** While the gizmo moves something: what, and the move so far (drawn live). */
  private moving: { key: string; W: Rigid } | null = null;
  /** A pole's control points shown as handles (a click on one moves it). */
  private ctrls = false;
  /** The list's kinds opened (the many: boxes, points, voxels start closed). */
  private open = new Set(['Planes', 'Poles', 'Surfaces']);
  /** The card's new shape: its kind, and the settings per kind (kept while the page is open). */
  private shKind: ShapeKind = 'pole';
  private shParams: Record<ShapeKind, Record<string, string | number>> = {
    pole: Object.fromEntries(SHAPE_PARAMS.pole.map(q => [q.key, q.def])), surface: Object.fromEntries(SHAPE_PARAMS.surface.map(q => [q.key, q.def])),
  };
  /** The picked shape's settings for Fit again (its own to start with), until another is picked. */
  private draft: { name: string; kind: ShapeKind; p: Record<string, string | number> } | null = null;

  constructor(private h: ConsHost, private $: (id: string) => HTMLElement) {
    const card = $('cons');
    // Delete in the card stays in it (on the page it deletes the selected points)
    card.addEventListener('keydown', e => { if (e.code === 'Delete' || e.code === 'Backspace') e.stopPropagation(); });
    ($('cplfit') as HTMLButtonElement).onclick = () => this.fitPlane((this.$('cplname') as HTMLInputElement).value.trim());
    (this.$('eplshow') as HTMLInputElement).onchange = () => this.draw();
    $('cshkind').addEventListener('click', e => {
      const k = (e.target as HTMLElement).dataset.k as ShapeKind | undefined;
      if (k && k !== this.shKind) { this.shKind = k; this.shapeForm(); }
    });
    ($('cshfit') as HTMLButtonElement).onclick = () => this.fitShape(this.shKind, (this.$('cshname') as HTMLInputElement).value.trim(), this.shParams[this.shKind]);
    this.shapeForm();
  }

  /** The card's new-shape row: the kind chosen and its settings. */
  private shapeForm() {
    for (const b of this.$('cshkind').querySelectorAll<HTMLElement>('button')) b.classList.toggle('on', b.dataset.k === this.shKind);
    this.$('cshparams').replaceChildren(...shapeParamFields(this.shKind, this.shParams[this.shKind]));
  }

  /** The tab came to the front (on) or went behind. */
  setOn(on: boolean) {
    if (on === this.on) return;
    this.on = on;
    if (on) this.restore = this.h.showAll();
    else { this.restore?.(); this.restore = null; this.pick = null; this.ctrls = false; }
    // the north arrow / scale bar and front only go with the tab in front (a plane is fitted to a lasso)
    const home = on ? this.$('construction') : this.$('viewport');
    for (const id of ['hud', 'vsel']) home.append(this.$(id));
    this.h.redraw();
  }

  // ── what there is ──────────────────────────────────────────────────────

  /** The planes in the loaded scans (a name in two scans: the newest). */
  planes() {
    const out = new Map<string, { scan: string; plane: PlaneDoc; t: string }>();
    for (const s of this.h.scans()) for (const [name, x] of s.state.planes) { const c = out.get(name); if (!c || x.t > c.t) out.set(name, { scan: s.id, plane: x.plane, t: x.t }); }
    return new Map([...out].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })));
  }

  /** The one-off shapes in the loaded scans. */
  private shapes() {
    const out = new Map<string, { scan: string; shape: ShapeDoc }>();
    for (const s of this.h.scans()) for (const x of s.state.shapes.values()) if (x.of === '') out.set(x.name, { scan: s.id, shape: x.shape });
    return new Map([...out].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })));
  }

  private frameOf = (ref: string) => +ref.slice(2) || 0;
  private planeM = (scan: string, d: PlaneDoc) => this.h.place(scan, this.frameOf(d.ref));
  private shapeM = (scan: string, d: ShapeDoc) => this.h.place(scan, this.frameOf(d.ref));
  private scanName = (id: string) => this.h.scans().find(s => s.id === id)?.name ?? id;
  private stale = (id: string) => !!this.h.scans().find(s => s.id === id)?.stale;

  // ── drawing ────────────────────────────────────────────────────────────

  /** The planes (Shapes tab's "planes" ticked), and in the tab what is picked (on top, white), a move in progress, a pole's handles; and the card (unless lines only). */
  draw(linesOnly = false) {
    const v = this.h.v;
    v.removeLines('eplane:'); v.removeLines('ecsel'); v.removeLines('ecctl');
    if ((this.$('eplshow') as HTMLInputElement).checked) {
      for (const [name, { scan, plane }] of this.planes()) {
        const key = `${scan}|${name}`;
        if (this.hidden.has(key)) continue;
        v.setLines(`eplane:${key}`, cg.planeLines(plane, this.planeM(scan, plane)), this.h.colour('plane'), true, true, scan);
      }
    }
    if (this.on) {
      const sel = this.pickedLines();
      if (sel) v.setLines('ecsel', sel, SEL_RGBA, true, false);
      const p = this.pick, sh = p?.k === 'shape' ? this.shapes().get(p.name) : null;
      if (this.ctrls && sh && sh.shape.kind === 'pole') {
        const out: number[] = [], r = 0.03;
        for (const c of cg.poleCtrls(sh.shape, this.shapeM(sh.scan, sh.shape)))
          for (let k = 0; k < 3; k++) { const d: V3 = [0, 0, 0]; d[k] = r; out.push(...add(c.p, d), ...add(c.p, [-d[0], -d[1], -d[2]])); }
        v.setLines('ecctl', new Float32Array(out), CTRL_RGBA, true, false);
      }
    }
    if (this.on && !linesOnly) this.list();
  }

  /** The picked thing's lines in the world (as the move in progress has it), or null. */
  private pickedLines(): Float32Array | null {
    const p = this.pick, W = this.moving && this.moving.key === this.key(p) ? this.moving.W : identity();
    if (!p) return null;
    if (p.k === 'plane') { const x = this.planes().get(p.name); return x ? cg.planeLines(x.plane, compose(W, this.planeM(x.scan, x.plane))) : null; }
    if (p.k === 'shape') { const x = this.shapes().get(p.name); return x ? shapeLines(x.shape, compose(W, this.shapeM(x.scan, x.shape))) : null; }
    if (p.k === 'box') {
      const b = this.h.typeBox(p.type);
      return b ? boxEdges(boxCorners(compose(W, compose(this.h.moved(p.name), cg.boxOn(b.box, this.h.info(), p.name))), b.box.s)) : null;
    }
    if (p.k === 'point') {
      const out: number[] = [], r = 0.06;
      for (const m of this.h.points().get(p.name) ?? []) for (let k = 0; k < 3; k++) { const d: V3 = [0, 0, 0]; d[k] = r; out.push(...add(m.q, d), ...add(m.q, [-d[0], -d[1], -d[2]])); }
      return out.length ? new Float32Array(out) : null;
    }
    return null;
  }

  private key = (p: Pick | null) => (p ? `${p.k}|${'name' in p ? p.name : p.type}` : '');

  // ── picking ────────────────────────────────────────────────────────────

  /** A click in the view while the tab is in front: a pole handle moves; else the nearest line's thing is picked. False: nothing there. */
  tap(x: number, y: number): boolean {
    if (!this.on) return false;
    const sc = (p: V3) => this.h.screen(p);
    const p = this.pick, sh = p?.k === 'shape' ? this.shapes().get(p.name) : null;
    if (this.ctrls && sh && sh.shape.kind === 'pole') {
      const M = this.shapeM(sh.scan, sh.shape);
      let best: { k: number; i: number; p: V3 } | null = null, bd = 10;
      for (const c of cg.poleCtrls(sh.shape, M)) { const s = sc(c.p), d = s ? Math.hypot(s[0] - x, s[1] - y) : Infinity; if (d < bd) { bd = d; best = c; } }
      if (best) { this.moveCtrl((p as { name: string }).name, best.k, best.i); return true; }
    }
    let hit: Pick | null = null, bd = 8;
    const near = (d: number, pk: Pick) => { if (d < bd) { bd = d; hit = pk; } };
    for (const [m, ms] of this.h.points()) for (const q of ms) { const s = sc(q.q); if (s) near(Math.hypot(s[0] - x, s[1] - y), { k: 'point', name: m }); }
    if ((this.$('eplshow') as HTMLInputElement).checked)
      for (const [name, { scan, plane }] of this.planes()) if (!this.hidden.has(`${scan}|${name}`)) near(cg.segDist(cg.planeLines(plane, this.planeM(scan, plane)), sc, x, y), { k: 'plane', name, scan });
    for (const [name, { scan, shape }] of this.shapes()) near(cg.segDist(shapeLines(shape, this.shapeM(scan, shape)), sc, x, y), { k: 'shape', name, scan });
    for (const b of this.h.boxes()) near(cg.segDist(boxEdges(b.c), sc, x, y), { k: 'box', name: b.name, type: b.type });
    if (!hit) { if (this.pick) { this.select(null); } return false; }
    this.select(hit);
    return true;
  }

  private select(p: Pick | null) {
    if (this.key(p) !== this.key(this.pick)) this.ctrls = false;
    this.pick = p;
    this.draw();
  }

  // ── the card: the list and the inspector ──────────────────────────────

  private list() {
    const box = this.$('clist'), ins = this.$('cinsp');
    box.textContent = '';
    const row = (pk: Pick, name: string, n: string, colour: string, hid?: { on: boolean; flip: () => void }) => {
      const r = el('div', { class: 'layer' + (hid && !hid.on ? ' off' : '') + (this.key(pk) === this.key(this.pick) ? ' sel' : '') });
      const eye = el('button', { class: 'eye', title: hid ? (hid.on ? 'hide' : 'show') : '' }, hid ? (hid.on ? '●' : '◌') : '●');
      eye.style.color = colour;
      if (hid) eye.onclick = e => { e.stopPropagation(); hid.flip(); this.draw(); };
      const nm = el('span', { class: 'name', title: 'pick it' }, name);
      r.onclick = () => this.select(pk);
      r.append(eye, el('span'), nm, el('span', { class: 'n' }, n), el('span'));
      return r;
    };
    const css = (c: number[]) => `rgb(${c.slice(0, 3).map(v => Math.round(255 * v)).join(' ')})`;
    // a kind's heading folds it; what is picked shows in it either way
    let fold = '';
    const head = (t: string, n: number) => {
      fold = t;
      if (!n) return;
      const h = el('div', { class: 'help chead', title: this.open.has(t) ? 'fold' : 'unfold' }, `${this.open.has(t) ? '▾' : '▸'} ${t} (${n})`);
      h.onclick = () => { if (this.open.has(t)) this.open.delete(t); else this.open.add(t); this.list(); };
      box.append(h);
    };
    const add = (pk: Pick, r: HTMLElement) => { if (this.open.has(fold) || this.key(pk) === this.key(this.pick)) box.append(r); };
    const planes = this.planes(), shapes = this.shapes(), boxes = this.h.boxes(), points = this.h.points(), vox = this.h.voxTypes();
    head('Planes', planes.size);
    for (const [name, { scan, plane }] of planes) {
      const k = `${scan}|${name}`, [w, hh] = cg.planeSize(plane);
      add({ k: 'plane', name, scan }, row({ k: 'plane', name, scan }, name, `${w.toFixed(2)} × ${hh.toFixed(2)} m`, css(this.h.colour('plane')),
        { on: !this.hidden.has(k), flip: () => { if (this.hidden.has(k)) this.hidden.delete(k); else this.hidden.add(k); } }));
    }
    const poles = [...shapes].filter(([, x]) => x.shape.kind === 'pole'), surfs = [...shapes].filter(([, x]) => x.shape.kind === 'surface');
    head('Poles', poles.length);
    for (const [name, { scan, shape }] of poles) add({ k: 'shape', name, scan }, row({ k: 'shape', name, scan }, name, `${shape.pieces.length} piece${shape.pieces.length === 1 ? '' : 's'}`, css(this.h.colour('pole'))));
    head('Surfaces', surfs.length);
    for (const [name, { scan, shape }] of surfs) add({ k: 'shape', name, scan }, row({ k: 'shape', name, scan }, name, `${shape.pieces.length} piece${shape.pieces.length === 1 ? '' : 's'}`, css(this.h.colour('surface'))));
    head('Boxes', boxes.length);
    for (const b of boxes) add({ k: 'box', name: b.name, type: b.type }, row({ k: 'box', name: b.name, type: b.type }, b.name, b.type, '#ffd84d'));
    head('Named points', points.size);
    for (const [name, ms] of points) add({ k: 'point', name }, row({ k: 'point', name }, name, `${ms.length} frame${ms.length === 1 ? '' : 's'}`, '#ffffff'));
    head('Voxel models', vox.length);
    for (const t of vox) add({ k: 'vox', type: t }, row({ k: 'vox', type: t }, t, 'voxels', '#9ad'));
    if (!planes.size && !shapes.size && !boxes.length && !points.size && !vox.length) box.append(el('div', { class: 'help' }, 'nothing built yet: fit a plane, pole or surface to a selection'));
    ins.textContent = '';
    if (this.pick) this.inspect(ins, this.pick);
  }

  private inspect(box: HTMLElement, p: Pick) {
    const title = (t: string, sub: string) => box.append(el('div', { class: 'pmhead' }, t), el('div', { class: 'help' }, sub));
    const rowOf = (...xs: HTMLElement[]) => { const r = el('div', { class: 'row' }); r.append(...xs); box.append(r); return r; };
    if (p.k === 'plane') {
      const x = this.planes().get(p.name);
      if (!x) { this.pick = null; return; }
      const d = x.plane, [w, h] = cg.planeSize(d), n = cg.planeNormal(d), M = this.planeM(x.scan, d), c = apply(M, cg.planeCentre(d));
      title(`▭ ${p.name}`, `plane in ${this.scanName(x.scan)}${this.frameOf(d.ref) ? `, frame ${this.frameOf(d.ref)}` : ''}; fitted to ${d.n.toLocaleString()} points; ` +
        `centre ${c.map(v => v.toFixed(2)).join(' ')} m, normal ${n.map(v => v.toFixed(2)).join(' ')}`);
      const set = (label: string, nd: PlaneDoc) => this.setPlane(p.name, x.scan, nd, label);
      rowOf(num('w', w, v => set(`size ${cm(v)} × ${cm(h)} cm`, cg.setPlaneSize(d, v, h)), 'width, along its own u'),
        num('h', h, v => set(`size ${cm(w)} × ${cm(v)} cm`, cg.setPlaneSize(d, w, v)), 'height, along its own v'));
      const tilt = (k: 0 | 1, deg: number) => btn(`${k ? 'v' : 'u'} ${deg > 0 ? '+' : ''}${deg}°`, `tilt it about its own ${k ? 'v' : 'u'} axis (through its centre)`, () => set(`tilt ${deg}° about ${k ? 'v' : 'u'}`, cg.tiltPlane(d, k, deg)));
      rowOf(el('span', { class: 'help' }, 'tilt'), tilt(0, -5), tilt(0, 5), tilt(1, -5), tilt(1, 5),
        btn('Square', 'stand it upright (or lay it flat), whichever it is nearer', () => set('square', cg.squarePlane(d))));
      rowOf(btn('Move', 'move / turn it with the gizmo', () => this.movePlane(p.name)),
        btn('Fit again', 'fit it to the selected points again', () => this.fitPlane(p.name)),
        btn('Rename', 'give it another name', () => this.rename(p)),
        btn('✕', 'drop the plane', () => this.drop(p)));
    } else if (p.k === 'shape') {
      const x = this.shapes().get(p.name);
      if (!x) { this.pick = null; return; }
      const d = x.shape, pole = d.kind === 'pole', pcs = d.pieces;
      title(`${pole ? '│' : '▱'} ${p.name}`, `${d.kind} of ${pcs.length} piece${pcs.length === 1 ? '' : 's'} in ${this.scanName(x.scan)}, fitted to ${d.n.toLocaleString()} points`);
      const set = (label: string, nd: ShapeDoc) => this.setShape(p.name, x.scan, nd, label);
      if (pole) {
        const r = Math.max(...pcs.flatMap(q => (isPolePiece(q) ? q.r : [])));
        rowOf(num('radius', r, v => set(`radius ${cm(v)} cm`, cg.setPoleRadius(d, v)), 'its largest radius (a taper keeps its shape)'));
      } else {
        const t = Math.max(...pcs.map(q => (isPolePiece(q) ? 0 : q.t)));
        rowOf(num('thickness', t, v => set(`thickness ${cm(v)} cm`, cg.setSurfaceThickness(d, v)), 'every piece\'s thickness', 0));
      }
      const r = rowOf(btn('Move', 'move / turn it with the gizmo', () => this.moveShape(p.name)));
      if (pole) {
        const b = btn(this.ctrls ? 'Points ✓' : 'Points', 'show its centreline\'s control points: click one to move it with the gizmo', () => { this.ctrls = !this.ctrls; this.draw(); });
        r.append(b);
      }
      r.append(btn('Rename', 'give it another name', () => this.rename(p)), btn('✕', `drop the ${d.kind}`, () => this.drop(p)));
      // fit it again to the selection: its kind and settings (as it was fitted, until changed here)
      if (this.draft?.name !== p.name) this.draft = { name: p.name, kind: d.kind, p: { ...this.defaults(d.kind), ...(d.p ?? {}) } };
      const dr = this.draft, kinds = el('span', { class: 'seg' });
      for (const k of ['pole', 'surface'] as ShapeKind[]) {
        const b = btn(k === 'pole' ? 'Pole' : 'Surface', `fit it again as a ${k}`, () => {
          if (dr.kind === k) return;
          dr.kind = k; dr.p = { ...this.defaults(k), ...(k === d.kind ? d.p ?? {} : {}) };
          this.list();
        });
        b.classList.toggle('on', dr.kind === k);
        kinds.append(b);
      }
      rowOf(kinds, btn('Fit again', 'fit it again to the selected points with these settings (one Undo)', () => this.fitShape(dr.kind, p.name, dr.p)));
      const pr = el('div', { class: 'erule on' });
      pr.append(...shapeParamFields(dr.kind, dr.p));
      box.append(pr);
    } else if (p.k === 'box') {
      const b = this.h.typeBox(p.type);
      if (!b) { this.pick = null; return; }
      const s = b.box.s, set = (k: number) => (v: number) => { const n = [...s] as V3; n[k] = v; this.setBox(p.type, cg.boxSized(b.box, n), `size ${n.map(cm).join(' × ')} cm`); };
      title(`▢ ${p.name}`, `the ${p.type} box, on this instance (every ${p.type} has it, each through its fit)`);
      rowOf(num('x', s[0], set(0)), num('y', s[1], set(1)), num('z', s[2], set(2)));
      rowOf(btn('Move instance', `move / turn this ${p.type} with the gizmo (its points hide, its voxels or the ${p.type} master move in their place); applies when published`, () => this.h.moveInstance(p.name)),
        btn('Move box', `move / turn the ${p.type} box with the gizmo, here on this instance: every ${p.type}'s box moves the same way on it`, () => this.moveBox(p.name, p.type)),
        btn('Clear', `clear the ${p.type} box`, () => this.setBox(p.type, null, 'clear')),
        btn(`Edit ${p.type}`, 'the object editor on the type', () => this.h.openType(p.type)));
    } else if (p.k === 'point') {
      const ms = this.h.points().get(p.name);
      if (!ms) { this.pick = null; return; }
      title(`✛ ${p.name}`, `named point in ${ms.length} frame${ms.length === 1 ? '' : 's'}`);
      for (const m of ms) rowOf(el('span', { class: 'help' }, `${this.scanName(m.scan)} · frame ${m.f}`),
        btn('Move', 'move this frame\'s point with the gizmo', () => this.h.movePoint(p.name, m.scan, m.f)));
      rowOf(btn('Rename', 'give it another name (in every frame)', () => this.rename(p)), btn('✕', 'drop the named point', () => this.h.dropPoint(p.name)));
    } else {
      title(`▦ ${p.type}`, 'voxel model, built on the object page');
      rowOf(btn(`Edit ${p.type}`, 'the object editor on the type', () => this.h.openType(p.type)));
    }
  }

  // ── edits ──────────────────────────────────────────────────────────────

  private ok(scan: string) {
    if (this.h.waits()) return false;
    if (this.stale(scan)) { this.h.say(`${this.scanName(scan)} is read-only (its source changed)`, true); return false; }
    return true;
  }

  private setPlane(name: string, scan: string, d: PlaneDoc, what: string) {
    if (!this.ok(scan)) return;
    this.h.commit(`plane "${name}": ${what}`, new Map([[scan, [{ op: 'plane.set', name, plane: roundPlane(d) } as OpBody]]]));
  }

  private setShape(name: string, scan: string, d: ShapeDoc, what: string) {
    if (!this.ok(scan)) return;
    this.h.commit(`${d.kind} "${name}": ${what}`, new Map([[scan, [{ op: 'shape.set', of: '', name, shape: roundDoc(d) } as OpBody]]]));
  }

  private setBox(type: string, box: TypeBox | null, what: string) {
    const b = this.h.typeBox(type);
    if (!b || this.h.waits()) return;
    this.h.commit(`${type} box: ${what}`, new Map(b.scans.map(s => [s, [{ op: 'type.box', of: type, box } as OpBody]])));
  }

  /** Fit a plane (name: a new one, or one to fit again) to the selected points; it is kept in the frame most came from. */
  private fitPlane(name: string) {
    if (this.h.waits()) return;
    const f = cg.planeFit(this.planes(), name, this.h.selection(PLANE_MAX_PTS));
    if (typeof f === 'string') { this.h.say(f, !f.startsWith('select')); return; }
    this.h.commit(`${f.again ? 'fit again' : 'fit'} plane "${f.name}"`, f.bodies);
    this.pick = { k: 'plane', name: f.name, scan: f.scan };
    (this.$('cplname') as HTMLInputElement).value = '';
    this.h.idle().then(() => this.h.say(f.said));
  }

  private defaults = (k: ShapeKind) => Object.fromEntries(SHAPE_PARAMS[k].map(q => [q.key, q.def]));

  /** Fit a pole or surface (name: a new one, or one to fit again) to the selected points; it is picked once fitted. */
  private fitShape(kind: ShapeKind, name: string, p: Record<string, string | number>) {
    if (this.h.waits()) return;
    const shapes = this.shapes();
    if (!name) { let k = 1; while (shapes.has(`${kind} ${k}`)) k++; name = `${kind} ${k}`; }
    if (!GROUP_NAME.test(name)) { this.h.say('name the shape: letters, digits, space . : + -', true); return; }
    this.h.fitShape(kind, name, p, this.$('cshfit').parentElement!).then(scan => {
      if (!scan) return;
      (this.$('cshname') as HTMLInputElement).value = '';
      this.draft = null;
      this.select({ k: 'shape', name, scan });
    });
  }

  /** Open the gizmo at c for the picked thing (drawn live as it goes); apply gets the world move. */
  private gizmo(c: V3, title: string, done: (W: Rigid) => void, square = false) {
    const key = this.key(this.pick), end = () => { this.moving = null; this.draw(); };
    this.moving = { key, W: identity() };
    this.h.gizmo(c, title, {
      change: W => { if (this.moving) { this.moving.W = W; this.draw(true); } },
      apply: W => { end(); done(W); },
      cancel: end,
    }, { square });
  }

  private movePlane(name: string) {
    const x = this.planes().get(name);
    if (!x || !this.ok(x.scan)) return;
    const M = this.planeM(x.scan, x.plane);
    this.gizmo(apply(M, cg.planeCentre(x.plane)), `Move plane "${name}"`, W => this.setPlane(name, x.scan, cg.transformPlane(x.plane, cg.inFrame(M, W)), 'moved'));
  }

  private moveShape(name: string) {
    const x = this.shapes().get(name);
    if (!x || !this.ok(x.scan)) return;
    const M = this.shapeM(x.scan, x.shape);
    this.gizmo(apply(M, cg.shapeCentre(x.shape)), `Move ${x.shape.kind} "${name}"`, W => this.setShape(name, x.scan, cg.transformShape(x.shape, cg.inFrame(M, W)), 'moved'));
  }

  /** Move one of a pole's control points (the square gizmo, as a named point moves). */
  private moveCtrl(name: string, k: number, i: number) {
    const x = this.shapes().get(name);
    if (!x || !this.ok(x.scan)) return;
    const M = this.shapeM(x.scan, x.shape), c = cg.poleCtrls(x.shape, M).find(q => q.k === k && q.i === i);
    if (!c) return;
    this.gizmo(c.p, `Move a control point of "${name}"`, W => {
      this.setShape(name, x.scan, cg.movePoleCtrl(x.shape, k, i, apply(inverse(M), apply(W, c.p))), `control point ${i + 1} moved`);
    }, true);
  }

  private moveBox(name: string, type: string) {
    const b = this.h.typeBox(type);
    if (!b || this.h.waits()) return;
    // drawn where the instance will be published (Pd): the move, back in the instance's own place, is Pd⁻¹ · W · Pd
    const info = this.h.info(), O = cg.boxOn(b.box, info, name), Pd = this.h.moved(name);
    this.gizmo(apply(Pd, [...O.t] as V3), `Move the ${type} box`, W => this.setBox(type, cg.boxMovedOn(b.box, info, name, compose(inverse(Pd), compose(W, Pd))), 'moved'));
  }

  private rename(p: Pick) {
    if (p.k === 'vox' || p.k === 'box') return;
    const to = prompt(`Rename "${p.name}" to`, p.name)?.trim();
    if (!to || to === p.name) return;
    if (!GROUP_NAME.test(to)) { this.h.say('a name is letters, digits, space . : + -', true); return; }
    if (p.k === 'plane') {
      const x = this.planes().get(p.name);
      if (!x || !this.ok(x.scan)) return;
      if (this.planes().has(to)) { this.h.say(`there is a plane "${to}" already`, true); return; }
      this.h.commit(`rename plane "${p.name}" to "${to}"`, new Map([[x.scan, [{ op: 'plane.drop', name: p.name } as OpBody, { op: 'plane.set', name: to, plane: x.plane } as OpBody]]]));
    } else if (p.k === 'shape') {
      const x = this.shapes().get(p.name);
      if (!x || !this.ok(x.scan)) return;
      if (this.shapes().has(to)) { this.h.say(`there is a shape "${to}" already`, true); return; }
      this.h.commit(`rename ${x.shape.kind} "${p.name}" to "${to}"`, new Map([[x.scan,
        [{ op: 'shape.drop', of: '', name: p.name } as OpBody, { op: 'shape.set', of: '', name: to, shape: x.shape } as OpBody]]]));
    } else {
      const pts = this.h.points(), ms = pts.get(p.name);
      if (!ms || this.h.waits()) return;
      if (pts.has(to)) { this.h.say(`there is a named point "${to}" already`, true); return; }
      const bodies = new Map<string, OpBody[]>();
      for (const scan of new Set(ms.map(m => m.scan))) {
        const mine = ms.filter(m => m.scan === scan), nw = mine[0].nw;
        bodies.set(scan, [
          { op: 'tree.mark', name: to, p: Object.fromEntries(mine.map(m => [`f:${m.f}`, markVal(m.mark)])), ...(nw !== 1 ? { w: nw } : {}) } as OpBody,
          { op: 'tree.mark', name: p.name, p: Object.fromEntries(mine.map(m => [`f:${m.f}`, null])) } as OpBody]);
      }
      this.h.commit(`rename named point "${p.name}" to "${to}"`, bodies);
    }
    this.pick = { ...p, name: to };
  }

  private drop(p: Pick) {
    if (p.k === 'plane') {
      const x = this.planes().get(p.name);
      if (!x || !this.ok(x.scan)) return;
      this.h.commit(`drop plane "${p.name}"`, new Map([[x.scan, [{ op: 'plane.drop', name: p.name } as OpBody]]]));
    } else if (p.k === 'shape') {
      const x = this.shapes().get(p.name);
      if (!x || !this.ok(x.scan)) return;
      this.h.commit(`drop ${x.shape.kind} "${p.name}"`, new Map([[x.scan, [{ op: 'shape.drop', of: '', name: p.name } as OpBody]]]));
    }
    this.pick = null;
  }
}

